import { useEffect, useMemo, useState } from 'react';
import { Package, Ruler, Scales } from '@phosphor-icons/react';
import styled from 'styled-components';
import { ItemImage, SkupremeModal } from '../../../components';
import {
  BoldText,
  CenterDropdownStyle,
  HoverBorderButton,
  MuteText,
  StyledButton,
} from '../../../elements';
import {
  CommonUtility,
  ErrorMessageConstant,
  theme,
  ToastMessage,
} from '../../../utility';
import { PackagingSearchInput } from './package-search';
import { PackagingService } from '../../../utility/services/packaging';
import { ConversionUtility } from '../../../utility/conversions';
import { sumContentsWeightInPounds } from '../../../utility/packaging/packed-weight';

const GreyBox = styled.div`
  background: ${({ theme }) => theme.colors.box};
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.colors.background};
  padding: 20px;
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 40vh;
  overflow: auto;
`;

const WhiteBox = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 5px;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.colors.background};
`;

const DimensionContainer = styled.div`
  display: flex;
  column-gap: 5px;
  align-items: center;
`;

const packageTypes = {
  custom: 'custom',
  mailer: 'mailer',
  shipsInProductPackaging: 'shipsInProductPackaging',
  strappedBundle: 'strappedBundle',
  shipInMasterCarton: 'shipInMasterCarton',
};

export function PackageCorrectionPopup({
  onClose,
  onLabelGenerated,
  open,
  order,
  packageIndex,
  pack,
  processing,
  toteId,
  warehouseId,
}) {
  const [packagingMaterial, setPackagingMaterial] = useState(null);
  const [type, setType] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentPackage = pack || {};

  const totalWeight = useMemo(() => {
    const entries = (currentPackage?.packedItems || []).map(item => ({
      weight: item?.productId?.package_dimensions?.weight,
      qty: item?.quantity,
    }));

    return {
      weight: ConversionUtility.convertToPoundsAndOunces(
        sumContentsWeightInPounds(entries),
      ),
      unit: 'lb',
    };
  }, [currentPackage?.packedItems]);

  const options = useMemo(() => {
    const arr = [
      {
        text: 'Packaging Boxes',
        value: packageTypes.custom,
        key: packageTypes.custom,
      },
      {
        text: 'Mailer',
        value: packageTypes.mailer,
        key: packageTypes.mailer,
      },
    ];

    const isSingleItem =
      currentPackage?.packedItems?.length === 1 &&
      currentPackage?.packedItems[0]?.quantity === 1;

    if (isSingleItem) {
      arr.push({
        text: 'Ships in Product Packaging',
        value: packageTypes.shipsInProductPackaging,
        key: packageTypes.shipsInProductPackaging,
      });
    } else {
      arr.push({
        text: 'Ship In Master Carton',
        key: packageTypes.shipInMasterCarton,
        value: packageTypes.shipInMasterCarton,
      });
    }

    arr.push({
      text: 'Strapped Bundle',
      value: packageTypes.strappedBundle,
      key: packageTypes.strappedBundle,
    });

    return arr;
  }, [currentPackage?.packedItems]);

  const showPackagingMaterial = useMemo(() => type === 'custom', [type]);

  const checkIfLabelCorrectionNeeded = () => {
    const label = currentPackage?.label;
    const metadata = label?.metadata || {};
    const currentMaterialId = label?.packagingMaterial?._id;
    const selectedMaterialId = packagingMaterial?._id;

    const previousType = metadata.mailer
      ? packageTypes.mailer
      : metadata.strapped
        ? packageTypes.strappedBundle
        : metadata.shipsInProductPackaging
          ? packageTypes.shipsInProductPackaging
          : metadata.shipInMasterCarton
            ? packageTypes.shipInMasterCarton
            : packageTypes.custom;

    if (previousType !== type) {
      if (
        [previousType, type].includes(packageTypes.mailer) &&
        [previousType, type].includes(packageTypes.custom)
      ) {
        if (
          type === packageTypes.mailer &&
          packagingMaterial?.packagingType !== 'Envelope'
        ) {
          return true;
        }

        return currentMaterialId !== selectedMaterialId;
      }
      return true;
    }

    if (type === 'custom') {
      return currentMaterialId !== selectedMaterialId;
    }

    return false;
  };

  const prepareCorrectionPayload = () => {
    const itemsArray =
      currentPackage?.items
        ?.map(item => ({
          qty: item.qty,
          itemId: item.item_id,
        }))
        ?.filter(item => item.qty > 0) || [];

    const label = currentPackage?.label;

    const payload = {
      orderSpeed: label?.metadata?.shipping_speed || 'Standard',
      labelId: label?._id,
      orderId: order?._id,
      fulfillmentProviderId: order?.fulfillment_provider_id,
      toteId,
    };

    if (type === packageTypes.mailer) {
      payload.mailer = true;
      payload.items = itemsArray;
    } else if (type === packageTypes.strappedBundle) {
      payload.strappedBundle = true;
      payload.items = itemsArray;
    } else if (type === packageTypes.shipsInProductPackaging) {
      payload.shipsInProductPackaging = true;
      payload.items = itemsArray;
    } else if (type === packageTypes.shipInMasterCarton) {
      payload.shipInMasterCarton = true;
      payload.items = itemsArray;
    } else {
      payload.packages = [
        {
          packagingMaterialId: packagingMaterial?._id,
          pounds: totalWeight?.weight?.pounds || 0,
          ounces: totalWeight?.weight?.ounces || 0,
          items: itemsArray,
        },
      ];
    }

    return payload;
  };

  const onConfirm = async () => {
    try {
      setLoading(true);
      const shouldCorrectLabel = checkIfLabelCorrectionNeeded();

      if (shouldCorrectLabel) {
        const payload = prepareCorrectionPayload();
        const res = await PackagingService.lableCorrection(payload);
        if (CommonUtility.isValidObject(res?.data)) {
          onLabelGenerated(packageIndex, res?.data);
          onClose();
        } else {
          ToastMessage.error(ErrorMessageConstant.defaultAPIError);
        }
      } else {
        onClose();
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!pack) return;

    const label = currentPackage?.label;
    const metadata = label?.metadata;

    setPackagingMaterial(label?.packagingMaterial);

    if (CommonUtility.isValidObject(metadata)) {
      if (metadata?.mailer) {
        setType('mailer');
        return;
      }

      if (metadata?.strapped) {
        setType('strappedBundle');
        return;
      }

      if (metadata?.shipInMasterCarton) {
        setType('shipInMasterCarton');
        return;
      }

      if (metadata?.shipsInProductPackaging) {
        setType('shipsInProductPackaging');
        return;
      }
    }

    setType('custom');
  }, [open, pack]);

  const isValid = useMemo(() => {
    if (!type) return false;
    if (type === 'custom' && !packagingMaterial) return false;
    return true;
  }, [type, packagingMaterial]);

  useEffect(() => {
    const handleEnter = e => {
      if (e.key === 'Enter') {
        if (!open) return;
        if (isValid && !loading && !processing) {
          onConfirm();
        }
      }
    };

    if (open) {
      window.addEventListener('keydown', handleEnter);
    }

    return () => {
      window.removeEventListener('keydown', handleEnter);
    };
  }, [open, isValid, loading, processing, onConfirm]);

  return (
    <SkupremeModal
      open={open}
      title="Change package configuration"
      size="tiny"
      onClose={onClose}
    >
      <GreyBox className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <Package size={24} color={theme.colors.grey} />
            <h3 className="m-0 ml-2">Package {packageIndex + 1}</h3>
          </div>
          <div className="d-flex align-items-center">
            <Scales size={24} color={theme.colors.grey} />
            <DimensionContainer className="m-0 ml-2">
              <span>{CommonUtility.roundNumber(totalWeight?.weight?.pounds || 0)}</span>
              <MuteText>lbs</MuteText>
              <span>{CommonUtility.roundNumber(totalWeight?.weight?.ounces || 0)}</span>
              <MuteText>oz</MuteText>
            </DimensionContainer>
          </div>
        </div>
        <ItemsContainer>
          {currentPackage?.packedItems?.map(item => (
            <ItemRenderer key={item?._id} item={item} />
          ))}
        </ItemsContainer>
        <div className="mt-3">
          <MuteText>Package Configurations</MuteText>
          <CenterDropdownStyle
            placeholder="Select Type"
            className="mt-2"
            options={options}
            selection
            value={type}
            onChange={(e, { value }) => setType(value)}
          />
          {showPackagingMaterial && (
            <PackagingSearchInput
              inputClassName="w-100 my-3"
              warehouseId={warehouseId}
              onResultClick={setPackagingMaterial}
            />
          )}
          {showPackagingMaterial && packagingMaterial && (
            <WhiteBox>
              <div className="d-flex flex-column">
                <span>{packagingMaterial?.name}</span>
                <MuteText>{packagingMaterial?.packagingType}</MuteText>
              </div>
              <div>
                <DimensionContainer>
                  <Ruler size={14} color={theme.colors.grey} />
                  <span>{packagingMaterial?.dimensions?.length}</span>
                  <MuteText>x</MuteText>
                  <span>{packagingMaterial?.dimensions?.width}</span>
                  <MuteText>x</MuteText>
                  <span>{packagingMaterial?.dimensions?.height}</span>
                  <MuteText>({packagingMaterial?.dimensions?.uom})</MuteText>
                </DimensionContainer>
                <DimensionContainer>
                  <Scales size={14} color={theme.colors.grey} />
                  <span className="ml-1">{packagingMaterial?.weight?.value}</span>
                  <MuteText>{packagingMaterial?.weight?.uom}</MuteText>
                </DimensionContainer>
              </div>
            </WhiteBox>
          )}
        </div>
      </GreyBox>
      <div className="d-flex justify-content-end">
        <HoverBorderButton onClick={onClose}>Cancel</HoverBorderButton>
        <StyledButton
          loading={loading || processing}
          disabled={!isValid}
          className="ml-2"
          onClick={onConfirm}
        >
          Save
        </StyledButton>
      </div>
    </SkupremeModal>
  );
}

const ClippedMuteText = styled(MuteText)`
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

function ItemRenderer({ item }) {
  return (
    <div className="d-flex">
      <ItemImage
        item={item?.productId}
        displayImageKey="display_image"
        imageKey="p_images"
        size="mini"
      />
      <div className="d-flex flex-column ml-2">
        <ClippedMuteText>{item?.productId?.p_title}</ClippedMuteText>
        <BoldText>{item?.productId?.p_sku}</BoldText>
      </div>
    </div>
  );
}
